import { XStack, YStack } from '@mythical/ui'
import { PlanLevel, mapNameToTier } from 'app/constants/tiers'
import config from 'app/config'
import { Perk } from 'app/queries/perks/perks'
import { Frequency, Tier } from 'app/queries/tiers/member-tiers'
import { useStores } from 'app/store'
import { useState } from 'react'
import { SizableText } from 'tamagui'

import { Button } from '../Button'
import { MemberfulLink } from '../MemberfulLink'
import { Tag } from '../Tag/Tag'
import { PerksList } from './PerksList'
import { TierCard } from './TierCard'

const FREQUENCY_ORDER: Frequency[] = ['monthly', 'quarterly', 'annually']

const FREQUENCY_LABELS: Record<Frequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Yearly',
}

const FREQUENCY_SUFFIXES: Record<Frequency, string> = {
  monthly: '/mo',
  quarterly: '/qtr',
  annually: '/yr',
}

interface PerksPanelProps {
  memberTiers?: Tier[]
  perks?: Perk[]
  initialTierLevel?: PlanLevel
  initialFrequency?: Frequency
}

const PerksPanel = ({
  memberTiers = [],
  perks = [],
  initialTierLevel,
  initialFrequency,
}: PerksPanelProps) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const userTier = userStore?.user?.tier
    ? Number(mapNameToTier(userStore?.user?.tier))
    : null

  const activeTier = memberTiers?.find((t) => t.planLevel === userTier)
  const sortedTiers = [...memberTiers].sort((a, b) => a.planLevel - b.planLevel)

  // If activeTier exists, select one tier up, otherwise select the first tier
  const getInitialTier = (): Tier | null => {
    if (initialTierLevel) {
      const requestedTier = sortedTiers.find(
        (tier) => tier.planLevel === initialTierLevel,
      )
      if (requestedTier) return requestedTier
    }

    if (activeTier) {
      const nextTier = sortedTiers.find(
        (t) => t.planLevel === activeTier.planLevel + 1,
      )
      return nextTier ?? sortedTiers[0] ?? null
    }
    return sortedTiers[0] ?? null
  }

  const initialTier = getInitialTier()
  const availableFrequencies =
    initialTier?.memberfulPlans?.map((plan) => plan.type) ?? []
  const resolvedInitialFrequency: Frequency =
    initialFrequency && availableFrequencies.includes(initialFrequency)
      ? initialFrequency
      : availableFrequencies.includes('monthly')
      ? 'monthly'
      : availableFrequencies[0] ?? 'monthly'

  const [selectedFrequency, setSelectedFrequency] = useState<Frequency>(
    resolvedInitialFrequency,
  )

  const [selectedTier, setSelectedTier] = useState<Tier | null>(initialTier)

  if (!sortedTiers.length || !selectedTier) return null

  const frequencies = FREQUENCY_ORDER.filter((freq) =>
    selectedTier.memberfulPlans?.some((p) => p.type === freq),
  )

  const selectedPlan = selectedTier.memberfulPlans?.find(
    (p) => p.type === selectedFrequency,
  )

  const savingsMessage = selectedPlan?.savings
    ? `Save ${selectedPlan.savings}% with ${FREQUENCY_LABELS[selectedFrequency]}!`
    : null

  const onTierChange = (tier: Tier) => {
    if (tier.id === selectedTier.id) return
    setSelectedFrequency('monthly')
    setSelectedTier(tier)
  }

  const getDisplayPrice = (tier: Tier, isSelected: boolean) => {
    const frequency = isSelected ? selectedFrequency : 'monthly'
    const price =
      tier.memberfulPlans?.find((p) => p.type === frequency)?.price ?? '0'
    return `$${price}${FREQUENCY_SUFFIXES[frequency]}`
  }

  const getPerksForTier = (planLevel: number) =>
    perks.filter((p) => p.tiers.includes(planLevel)).map((p) => p.title)

  return (
    <YStack ai="center" gap="$6" w="100%">
      <YStack ai="center" gap="$3.5" $gtSm={{ gap: '$5' }}>
        <SizableText
          ff="$heading"
          size="$l"
          ta="center"
          maw={250}
          $gtSm={{ maw: '100%' }}
        >
          Join the Community of Fellow Mythical Beasts!
        </SizableText>
        <SizableText size="$m" color="$fgSecondary" fow="500" ta="center">
          Choose the plan that's right for you.
        </SizableText>
      </YStack>

      <YStack ai="center" gap="$2" $gtXs={{ px: '$5' }}>
        <XStack boc="$borderSecondary" bw="$1" br="$pill" p="$1.5" gap="$1">
          {frequencies.map((freq) => {
            const isActive = selectedFrequency === freq
            return (
              <Button
                key={freq}
                onPress={() => setSelectedFrequency(freq)}
                animation="quick"
                theme={isActive ? 'white' : 'transparent'}
                size="$3"
                h={32}
                fg={1}
                maw={160}
                fontSize="$m"
                $gtXxxs={{ miw: 100 }}
              >
                {FREQUENCY_LABELS[freq]}
              </Button>
            )
          })}
        </XStack>

        {savingsMessage && (
          <Tag displaySize="md" variant="mustard">
            {savingsMessage}
          </Tag>
        )}
      </YStack>

      <YStack gap="$5" w="100%" $gtSm={{ display: 'none' }}>
        <YStack gap="$2">
          {sortedTiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              isSelected={selectedTier.id === tier.id}
              price={getDisplayPrice(tier, selectedTier.id === tier.id)}
              onPress={() => onTierChange(tier)}
              disabled={tier.planLevel === userTier}
            />
          ))}
        </YStack>
        <PerksList
          perkTitles={getPerksForTier(selectedTier.planLevel)}
          disabled={selectedTier.planLevel === userTier}
        />
      </YStack>

      <YStack gap="$5" w="100%" display="none" $gtSm={{ display: 'flex' }}>
        <XStack gap="$2" ai="stretch">
          {sortedTiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              isSelected={selectedTier.id === tier.id}
              price={getDisplayPrice(tier, selectedTier.id === tier.id)}
              onPress={() => onTierChange(tier)}
              flex={1}
              flexBasis={0}
              disabled={tier.planLevel === userTier}
            />
          ))}
        </XStack>
        <XStack gap="$2" ai="stretch">
          {sortedTiers.map((tier) => (
            <PerksList
              key={tier.id}
              perkTitles={getPerksForTier(tier.planLevel)}
              flex={1}
              flexBasis={0}
              disabled={tier.planLevel === userTier}
            />
          ))}
        </XStack>
      </YStack>

      {canMemberfulLink && (
        <YStack
          ai="stretch"
          py="$2"
          width="100%"
          $gtSm={{ ai: 'center', py: '$5' }}
        >
          <MemberfulLink
            Component={Button}
            href={`${config.memberfulUrl}/checkout?plan=${selectedPlan?.externalId}`}
          >
            {`Join for $${selectedPlan?.price ?? '0'}${FREQUENCY_SUFFIXES[selectedFrequency]}`}
          </MemberfulLink>
        </YStack>
      )}
    </YStack>
  )
}

export default PerksPanel
